import { createFileRoute } from "@tanstack/react-router";

import type { BillingNotificationDelivery } from "@/lib/supabase.server";

function escapeHtml(value: string | null | undefined) {
  return (value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function mailCopy(notification: BillingNotificationDelivery) {
  if (notification.event === "invoice_issued" && notification.amount_minor === 0) {
    return {
      subject: `Ihr Begner-Beleg ${notification.invoice_number}`,
      heading: "Durch Rabatt ausgeglichen",
      message:
        "Ihr Angebot ist vollständig durch Rabatt ausgeglichen. Es ist keine Zahlung erforderlich.",
    };
  }
  if (notification.event === "invoice_issued") {
    return {
      subject: `Ihre Begner-Rechnung ${notification.invoice_number}`,
      heading: "Ihre Rechnung ist bereit",
      message: "Bitte begleichen Sie den Betrag bis zum angegebenen Zahlungsziel.",
    };
  }
  if (notification.event === "payment_reminder") {
    return {
      subject: `Zahlungserinnerung ${notification.invoice_number}`,
      heading: "Freundliche Zahlungserinnerung",
      message: "Das Zahlungsziel Ihrer Rechnung rückt näher.",
    };
  }
  if (notification.event === "invoice_overdue") {
    return {
      subject: `Rechnung überfällig: ${notification.invoice_number}`,
      heading: "Ihre Rechnung ist überfällig",
      message: "Bitte prüfen Sie die offene Rechnung und melden Sie sich bei Rückfragen.",
    };
  }
  if (notification.event === "invoice_voided") {
    return {
      subject: `Rechnung storniert: ${notification.invoice_number ?? "Entwurf"}`,
      heading: "Die Rechnung wurde storniert",
      message: "Für diese Rechnung ist keine Zahlung mehr geschuldet.",
    };
  }
  return {
    subject: `Gutschrift ${notification.credit_note_number}`,
    heading: "Ihre Gutschrift ist bereit",
    message: "Die vollständige Rückerstattung wurde verbucht.",
  };
}

function billingEmail(notification: BillingNotificationDelivery) {
  const copy = mailCopy(notification);
  const number =
    notification.event === "credit_note"
      ? notification.credit_note_number
      : notification.invoice_number;
  const amount = new Intl.NumberFormat("de-CH", {
    style: "currency",
    currency: notification.currency,
  }).format(notification.amount_minor / 100);
  const rebate = notification.discount_minor
    ? `Ausgangspreis: ${new Intl.NumberFormat("de-CH", { style: "currency", currency: notification.currency }).format((notification.subtotal_minor ?? notification.amount_minor) / 100)} · Rabatt ${notification.discount_code ?? "Aktion"}${notification.discount_code && notification.discount_percent ? ` (${notification.discount_percent} %)` : ""}: −${new Intl.NumberFormat("de-CH", { style: "currency", currency: notification.currency }).format(notification.discount_minor / 100)}`
    : "";
  const invoiceUrl = `${notification.site_url.replace(/\/$/, "")}/rechnung/${notification.public_token}`;
  const reference =
    notification.payment_reference &&
    (notification.invoice_status === "open" || notification.invoice_status === "overdue")
      ? `<br>Referenz: ${escapeHtml(notification.payment_reference)}`
      : "";
  const html = `<!doctype html><html><body style="margin:0;background:#f4f6f8;font-family:Arial,sans-serif;color:#1e293b"><div style="max-width:600px;margin:24px auto;background:white;border:1px solid #e2e8f0;border-radius:16px;overflow:hidden"><div style="background:#119dd4;color:white;padding:22px 28px;font-size:22px;font-weight:bold">Begner</div><div style="padding:28px"><h1 style="font-size:22px">${escapeHtml(copy.heading)}</h1><p>Guten Tag ${escapeHtml(notification.customer_name)}</p><p>${escapeHtml(copy.message)}</p><div style="background:#f8fafc;padding:16px;border-radius:12px"><strong>${escapeHtml(number)}</strong><br>${escapeHtml(amount)}${reference}${rebate ? `<br>${escapeHtml(rebate)}` : ""}</div><p style="margin-top:24px"><a href="${escapeHtml(invoiceUrl)}" style="display:inline-block;background:#119dd4;color:white;text-decoration:none;padding:12px 18px;border-radius:10px;font-weight:bold">Dokument öffnen</a></p><p style="font-size:12px;color:#64748b">Bei Fragen antworten Sie auf diese E-Mail.</p></div></div></body></html>`;
  const text = `${copy.heading}\n\nGuten Tag ${notification.customer_name}\n\n${copy.message}\n\n${number}\n${amount}${rebate ? `\n${rebate}` : ""}${reference ? `\nReferenz: ${notification.payment_reference}` : ""}\n\nDokument: ${invoiceUrl}`;
  return { ...copy, html, text };
}

export const Route = createFileRoute("/api/billing-email-cron")({
  staticData: { sitemap: false },
  server: {
    handlers: {
      POST: async ({ request }) => {
        const authorization = request.headers.get("authorization") ?? "";
        const token = authorization.startsWith("Bearer ") ? authorization.slice(7).trim() : "";
        if (token.length < 32 || token.length > 200) {
          return Response.json({ error: "Unauthorized" }, { status: 401 });
        }

        const { claimBillingNotifications, finishBillingNotification } =
          await import("@/lib/supabase.server");
        let notifications: BillingNotificationDelivery[];
        try {
          notifications = await claimBillingNotifications(token, 10);
        } catch {
          return Response.json({ error: "Unauthorized" }, { status: 401 });
        }

        const { sendDecisionEmail } = await import("@/lib/email/send.server");
        let sent = 0;
        let failed = 0;
        for (const notification of notifications) {
          try {
            const mail = billingEmail(notification);
            await sendDecisionEmail({
              to: notification.recipient_email,
              subject: mail.subject,
              html: mail.html,
              text: mail.text,
            });
            await finishBillingNotification({
              token,
              notificationId: notification.notification_id,
              success: true,
            });
            sent += 1;
          } catch (error) {
            await finishBillingNotification({
              token,
              notificationId: notification.notification_id,
              success: false,
              error: error instanceof Error ? error.message : "Unbekannter Versandfehler",
            }).catch(() => undefined);
            failed += 1;
          }
        }

        return Response.json(
          { processed: notifications.length, sent, failed },
          { headers: { "Cache-Control": "no-store" } },
        );
      },
    },
  },
});
